/**
 * The handle between two panels.
 *
 * Three dots in the middle of a thin column: the one place to take hold of a
 * panel edge, the same everywhere in the app. Drag it, or focus it and use the
 * arrow keys; double-clicking puts the panel back to its usual width.
 */

import { cn } from '@/lib/cn';

export function Grip({ dragging, label, className, ...rest }: {
  dragging: boolean;
  label: string;
  className?: string;
} & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...rest}
      aria-label={label}
      title="Drag to resize · double-click to reset"
      className={cn(
        'group/grip relative z-[2] flex w-3 flex-none cursor-col-resize touch-none items-center justify-center self-stretch outline-none',
        className
      )}
    >
      <span
        className={cn(
          'flex w-[8px] flex-col items-center justify-center gap-[5px] rounded-full transition-[background-color,height,box-shadow] duration-[250ms] ease-[var(--ease-out)]',
          dragging
            ? 'h-24 bg-accent-soft shadow-[0_0_0_1px_var(--accent-line)]'
            : 'h-14 bg-surface-2 shadow-[0_0_0_1px_var(--line-soft)] group-hover/grip:h-20 group-hover/grip:bg-surface-3 group-focus-visible/grip:h-20 group-focus-visible/grip:shadow-[0_0_0_2px_var(--accent)]'
        )}
      >
        {[0, 1, 2].map((i) => (
          <i
            key={i}
            aria-hidden
            className={cn(
              'block h-[4px] w-[4px] rounded-full transition-colors duration-200',
              dragging ? 'bg-accent' : 'bg-mute group-hover/grip:bg-ink group-focus-visible/grip:bg-accent'
            )}
          />
        ))}
      </span>
    </div>
  );
}
